import { sortBy } from "lodash-es";
import { defineStore } from "pinia";
import type { ComputedRef } from "vue";
import { computed, ref } from "vue";

import { getStickies } from "@/backend/read/BackendReader";
import { addSticky } from "@/backend/read/changeSticky";
import type { IdMap, InfoLevel } from "@/model/baseTypes";
import type { Board, BoardWithObjectives } from "@/model/board";
import { isBacklogBoard } from "@/model/board";
import type { BoardCard, Card, LinkedSticky } from "@/model/card";
import type {
  Link,
  LinkableCard,
  LinkableItem,
  LinkingCard,
  LinkingObjective,
  LinkingTarget,
  ObjectiveLink,
} from "@/model/link";
import type { LinkedObjective, Objective } from "@/model/objective";
import { isEmpty } from "@/utils/record";
import { asReadonly, asSemiReadonly } from "@/utils/vue";

import { useBoardStore } from "./board";
import { useBoardsStore } from "./boards";
import { useCardStore } from "./card";
import { linkableCardOf, linkId } from "./helpers/link";
import { calcLinkState } from "./helpers/linkState";
import { fadeOutCard, markLinkedCard, resetAllCardMarks } from "./helpers/mark";
import { useObjectiveStore } from "./objective";

export interface LinkedCards {
  link: Link;
  from: BoardCard;
  to: BoardCard;
}

export const useLinkStore = defineStore("link", () => {
  const links = ref<Link[]>([]);
  const linksById = ref<Record<Link["id"], Link>>({});
  const linksByFrom = ref<Record<Link["from"], Link[]>>({});
  const linksByTo = ref<Record<Link["to"], Link[]>>({});
  const markingObjectiveLinkedCards = ref<Objective["id"] | null>(null);
  const markingCardsLinkedCards = ref<BoardCard[] | null>(null);
  const linkGroups = ref<Record<string, IdMap<BoardCard>>>({});
  const linkStates = new Map<Link["id"], ComputedRef<InfoLevel>>();

  const isMarkingLinks = computed(
    () =>
      !!markingObjectiveLinkedCards.value ||
      (markingCardsLinkedCards.value !== null && markingCardsLinkedCards.value.length > 0),
  );

  function cardsByLink(linkTargetId: string): Card[] {
    const groups = linkGroups.value[linkTargetId];
    if (groups) {
      const res = new Array<Card>();
      for (const id in groups) {
        res.push(groups[id].data);
      }
      return res;
    }
    const card = useCardStore().cards[linkTargetId];
    if (!card) {
      // can happen because of REN-8846
      return [];
    }
    return [card];
  }

  function boardCardByLink(linkTargetId: string, board: Board): BoardCard | undefined {
    const groups = linkGroups.value[linkTargetId];
    return groups ? groups[board.id] : board.cards[linkTargetId];
  }

  function getLinkedCards(linkingTarget: LinkingTarget, withLinks = false): Card[] {
    const missingCards = new Array<string>();

    const resolveCards = (ids: string[]) =>
      ids
        .map((id) => {
          const card = cardByLink(id);
          if (!card) {
            // Assume the card is from another board
            missingCards.push(id);
            return null;
          }
          return card;
        })
        .filter((c): c is Card => !!c)
        .reverse();

    let cardLinks: Card[];

    if (linkingTarget.type === "sticky") {
      const linkedStickies = useCardStore().cards[linkingTarget.id]?.linkedStickies;
      if (!linkedStickies) return [];
      cardLinks = resolveCards(linkedStickies.map((l) => l.stickyId));
    } else {
      const linkedStickies = useObjectiveStore().objectiveById(linkingTarget.id, {
        boardId: linkingTarget.boardId,
      })?.cards;
      if (!linkedStickies) return [];
      cardLinks = resolveCards(linkedStickies.filter((c) => c.isOrigin).map((c) => c.id));
    }

    if (missingCards.length) loadStickies(missingCards);

    return cardLinks;

    // Load the missing stickies. This will add them to the store,
    // and Vue will re-call this function to render the full list.
    function loadStickies(ids: string[]) {
      void getStickies(ids, withLinks).then((stickies) => stickies.forEach(addSticky));
    }
  }

  // Representative card doc for a link-target id (group/origin id):
  // one of the group's mirrors when grouped, else the plain card.
  function cardByLink(linkTargetId: string): Card | undefined {
    const groups = linkGroups.value[linkTargetId];
    return groups ? Object.values(groups)[0].data : useCardStore().cards[linkTargetId];
  }

  function getLinkedItems(linkingTarget: LinkingTarget, withLinks = false): LinkableItem[] {
    return [
      ...getLinkedCardItems(linkingTarget, withLinks),
      ...(linkingTarget.type === "sticky"
        ? getLinkedObjectives(linkingTarget)
        : getLinkedObjectivesForObjective(linkingTarget)),
    ];
  }

  function getLinkedCardItems(linkingTarget: LinkingTarget, withLinks: boolean): LinkableCard[] {
    const cards = getLinkedCards(linkingTarget, withLinks);
    return cards.map((linkedCard: Card) => linkableCardOf(linkedCard, true));
  }

  function getLinkedObjectives(linkingCard: LinkingCard): LinkableItem[] {
    const card = useCardStore().cards[linkingCard.id];
    return card ? resolveLinkedObjectives(card.objectives) : [];
  }

  function getLinkedObjectivesForObjective(linkingObjective: LinkingObjective): LinkableItem[] {
    const objective = useObjectiveStore().objectiveById(linkingObjective.id, {
      boardId: linkingObjective.boardId,
    });
    return objective ? resolveLinkedObjectives(objective.linkedObjectives) : [];
  }

  const linksOnBoard = computed(() => {
    const linkMap = new Map<string, LinkedCards>();
    const board = useBoardStore().board;
    if (!board) {
      return [];
    }
    Object.values(board.cards).forEach((from) => {
      for (const link of from.data.linkedStickies) {
        if (link.direction === "from") {
          const to = boardCardByLink(link.stickyId, board);
          if (to) {
            linkMap.set(link.linkId, {
              link: { id: link.linkId, from: linkId(from.data), to: link.stickyId },
              from,
              to,
            });
          }
        }
      }
    });
    return [...linkMap.values()];
  });

  const riskyLinkCount = computed(() => {
    const counts = { risky: 0, critical: 0 };
    linksOnBoard.value.forEach((linked) => {
      const state = linkState(linked.link);
      if (state === "warn") {
        counts.risky++;
      }
      if (state === "error") {
        counts.critical++;
      }
    });
    return counts;
  });

  const hasRiskyLinks = computed(() => {
    const counts = riskyLinkCount.value;
    return counts.risky > 0 || counts.critical > 0;
  });

  /** The link's state from its two cards, "default" while a card is not loaded. Cached per link id. */
  function linkState({ id, from, to }: Link): InfoLevel {
    let state = linkStates.get(id);
    if (!state) {
      state = computed(() => {
        const fromCard = cardByLink(from);
        const toCard = cardByLink(to);
        return fromCard && toCard ? calcLinkState(fromCard, toCard) : "default";
      });
      linkStates.set(id, state);
    }
    return state.value;
  }

  function cardLinkState(card: Card, linked: LinkedSticky): InfoLevel {
    return linkState({ id: linked.linkId, from: linkId(card), to: linked.stickyId });
  }

  // Custom reset: most of the state is kept private, so it isn't part of $state
  // and the autoReset plugin couldn't restore it.
  function $reset() {
    setLinks([]);
    linkGroups.value = {};
    markingObjectiveLinkedCards.value = null;
    markingCardsLinkedCards.value = null;
    linkStates.clear();
  }

  function setLinks(newLinks: Link[]) {
    links.value = [];
    linksById.value = {};
    linksByFrom.value = {};
    linksByTo.value = {};
    for (const link of newLinks) {
      addLink(link);
    }
  }

  function addToLinkGroup(groupId: string, boardId: string, boardCard: BoardCard) {
    if (!linkGroups.value[groupId]) linkGroups.value[groupId] = {};
    linkGroups.value[groupId][boardId] = boardCard;
  }

  function removeFromLinkGroup(card: Card, boardId: string) {
    const groupId = card.groupId;
    if (!groupId) return;
    const group = linkGroups.value[groupId];
    if (group) {
      delete group[boardId];
      if (isEmpty(group)) delete linkGroups.value[groupId];
    }
  }

  function add(link: Link) {
    if (!linksById.value[link.id]) {
      addLink(link);
    }
  }

  function addLink(link: Link) {
    links.value.push(link);
    linksById.value[link.id] = link;
    addToIndex(linksByFrom.value, link.from, link);
    addToIndex(linksByTo.value, link.to, link);

    function addToIndex(map: Record<string, Link[]>, id: string, link: Link) {
      if (!(id in map)) {
        map[id] = [];
      }
      map[id].push(link);
    }
  }

  function remove(linkId: string) {
    const linkIndex = links.value.findIndex((link) => link.id === linkId);
    if (linkIndex >= 0) {
      removeLink(linkIndex);
    }
  }

  function removeLink(index: number) {
    const link = links.value[index];
    links.value.splice(index, 1);
    delete linksById.value[link.id];
    linkStates.delete(link.id);
    removeFromIndex(linksByFrom.value, link.from, link);
    removeFromIndex(linksByTo.value, link.to, link);
    return link;

    function removeFromIndex(map: Record<string, Link[]>, id: string, link: Link) {
      const i = map[id].findIndex((l) => l.id === link.id);
      if (i >= 0) {
        map[id].splice(i, 1);
      }
    }
  }

  function addObjectiveLink(
    cardId: Card["id"],
    linkInfo: { boardId: string; objectiveId: string },
  ) {
    const card = useCardStore().cards[cardId];
    if (!card) {
      return;
    }
    if (
      card.objectives.every(
        (cardObjective) =>
          cardObjective.boardId !== linkInfo.boardId || cardObjective.id !== linkInfo.objectiveId,
      )
    ) {
      card.objectives.push({ id: linkInfo.objectiveId, boardId: linkInfo.boardId });
    }
  }

  function removeObjectiveLink(
    cardId: Card["id"],
    linkInfo: Pick<ObjectiveLink, "objectiveId" | "boardId">,
  ) {
    const card = useCardStore().cards[cardId];
    if (!card) {
      return;
    }
    useCardStore().setObjectives(
      cardId,
      card.objectives.filter(
        (obj) => obj.boardId !== linkInfo.boardId || obj.id !== linkInfo.objectiveId,
      ),
    );
  }

  function updateCardLinkedMarks(): string[] {
    const cards = markingCardsLinkedCards.value;
    return cards && cards.length > 0 ? markCardLinkedCards(cards) : [];
  }

  function markCardLinkedCards(cards: BoardCard[]): string[] {
    markCardsLinkedCards(cards);
    const distances = linkedCardDistances(cards);
    const highlighted = new Array<{ dist: number; id: string }>();
    Object.values(useBoardStore().currentBoard().cards).forEach((boardCard) => {
      const id = boardCard.data.id;
      if (markLinkedCard(boardCard, distances[id])) {
        highlighted.push({ dist: distances[id], id });
      }
    });
    return sortBy(highlighted, (h) => -h.dist).map((h) => h.id);
  }

  function markCardsLinkedCards(cards: BoardCard[]) {
    markingObjectiveLinkedCards.value = null;
    markingCardsLinkedCards.value = cards;
  }

  // breadth first search from given cards following all links
  function linkedCardDistances(cards: BoardCard[]): IdMap<number> {
    const board = useBoardStore().currentBoard();
    const queue = cards.map((card) => ({ card, distance: 0 }));
    const linked: IdMap<number> = {};
    // it's safe to add to queue inside for/of
    for (const item of queue) {
      if (!(item.card.data.id in linked)) {
        linked[item.card.data.id] = item.distance;
        item.card.data.linkedStickies.forEach((link) => {
          addToQueue(link.stickyId, item.distance + 1);
        });
      }
    }
    return linked;

    function addToQueue(linkTargetId: string, distance: number) {
      const card = boardCardByLink(linkTargetId, board);
      if (card && !(card.data.id in linked)) {
        queue.push({ card, distance });
      }
    }
  }

  function markObjectiveLinkedCards(objectiveId: string) {
    markingCardsLinkedCards.value = null;
    markingObjectiveLinkedCards.value = objectiveId;

    Object.values(useBoardStore().currentBoard().cards).forEach((boardCard) => {
      const isLinkedToObjective = boardCard.data.objectives.some(
        (objective) => objective.id === objectiveId,
      );

      const isLinkedToBacklogItem = boardCard.data.linkedStickies.some((link) => {
        const linkedCard = cardByLink(link.stickyId);

        return (
          !!linkedCard &&
          isBacklogBoard(linkedCard.type.origin) &&
          linkedCard.objectives.some((objective) => objective.id === objectiveId)
        );
      });

      fadeOutCard(boardCard, !isLinkedToObjective && !isLinkedToBacklogItem);
    });
  }

  function removeAllMarks() {
    markingObjectiveLinkedCards.value = null;
    markingCardsLinkedCards.value = null;
    resetAllCardMarks(useBoardStore().currentBoard());
  }

  function removeCardLinkedMarks(cardId: string) {
    if (markingCardsLinkedCards.value?.some((card) => card.data.id === cardId)) {
      markingCardsLinkedCards.value = null;
      resetAllCardMarks(useBoardStore().currentBoard());
    }
  }

  function removeObjectiveLinkedMarks(objectiveId: string) {
    if (markingObjectiveLinkedCards.value === objectiveId) {
      markingObjectiveLinkedCards.value = null;
      resetAllCardMarks(useBoardStore().currentBoard());
    }
  }

  return {
    links: asReadonly(links),
    linksById: asReadonly(linksById),
    markingCardsLinkedCards: asSemiReadonly(markingCardsLinkedCards),

    linksByCard: (lnkId: string) => [
      ...(linksByFrom.value[lnkId] || []),
      ...(linksByTo.value[lnkId] || []),
    ],
    isPinned: (cardId: string) =>
      markingCardsLinkedCards.value?.some((card) => card.data.id === cardId) ?? false,
    isMarkingLinks,
    cardsByLink,
    boardCardByLink,
    getLinkedCards,
    cardByLink,
    getLinkedItems,
    linkState,
    cardLinkState,
    linksOnBoard,
    riskyLinkCount,
    hasRiskyLinks,

    $reset,
    setLinks,
    addToLinkGroup,
    removeFromLinkGroup,
    add,
    remove,
    addObjectiveLink,
    removeObjectiveLink,
    updateCardLinkedMarks,
    markCardLinkedCards,
    markObjectiveLinkedCards,
    removeAllMarks,
    removeCardLinkedMarks,
    removeObjectiveLinkedMarks,
  };
});

/**
 * Resolve a list of objective references (e.g. a card's `objectives` or an
 * objective's `linkedObjectives`) into linkable objective items, dropping any
 * reference whose objective is no longer loaded. Newest first.
 */
function resolveLinkedObjectives(refs: readonly LinkedObjective[]): LinkableItem[] {
  return refs
    .flatMap((ref) => {
      const board = useBoardsStore().boards[ref.boardId] as BoardWithObjectives;
      const boardObjective = [
        ...(board?.objectives ?? []),
        ...(board?.stretchObjectives ?? []),
      ].find(({ id }) => ref.id === id);
      if (!boardObjective) {
        return [];
      }

      return {
        kind: "objective" as const,
        linked: true,
        id: ref.id,
        text: boardObjective.text,
        description: boardObjective.description,
        boardId: ref.boardId,
        type: useObjectiveStore().objectiveTypeById(ref.id, { boardId: ref.boardId }),
      };
    })
    .reverse();
}
